import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, CalendarBlank, CaretDown } from '@phosphor-icons/react';
import { setPageMeta, breadcrumbLd } from '../utils/seo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  homeowner: 'bg-[#2B4C6F]',
  contractor: 'bg-[#D95800]',
  tools: 'bg-emerald-700',
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    setPost(null);
    setError(false);
    axios.get(`${API_URL}/api/blog/posts/${slug}`)
      .then((r) => {
        const p = r.data.post;
        setPost(p);
        setRelated(r.data.related || []);
        setPageMeta({
          title: `${p.title} | ConstructHelp Blog`,
          description: p.meta_description,
          ogType: 'article',
          url: `${window.location.origin}/blog/${p.slug}`,
          image: p.cover_image ? `${API_URL}${p.cover_image}` : undefined,
          jsonLd: [
            {
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: p.title,
              description: p.meta_description,
              datePublished: p.created_at,
              ...(p.cover_image ? { image: `${API_URL}${p.cover_image}` } : {}),
              author: { '@type': 'Organization', name: 'ConstructHelp' },
              publisher: { '@type': 'Organization', name: 'ConstructHelp' },
            },
            ...(p.faqs?.length ? [{
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: p.faqs.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            }] : []),
            breadcrumbLd([
              { name: 'Home', url: window.location.origin },
              { name: 'Blog', url: `${window.location.origin}/blog` },
              { name: p.title },
            ]),
          ],
        });
        window.scrollTo(0, 0);
      })
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center">
        <div className="text-center">
          <p className="font-black text-xl text-neutral-900 dark:text-white mb-4">Article not found</p>
          <Link to="/blog" className="text-[#D95800] font-bold underline">Back to the blog</Link>
        </div>
      </div>
    );
  }
  if (!post) {
    return <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center"><p className="font-bold text-neutral-500">Loading...</p></div>;
  }

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]" data-testid="blog-post-page">
      {/* Hero */}
      <div className="bg-[#0F172A] border-b-4 border-[#D95800]">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-neutral-400 hover:text-white text-xs font-bold uppercase tracking-wider mb-4 transition-colors" data-testid="back-to-blog-link">
            <ArrowLeft size={14} weight="bold" /> All Articles
          </Link>
          <div className="flex items-center gap-2 mb-3">
            <span className={`${CATEGORY_COLORS[post.category] || 'bg-neutral-700'} text-[10px] font-black uppercase tracking-widest text-white px-2 py-0.5 border border-black`}>
              {post.category_label}
            </span>
            <span className="text-[11px] text-neutral-400 flex items-center gap-1">
              <CalendarBlank size={12} weight="bold" /> {fmtDate(post.created_at)}
            </span>
          </div>
          <h1 className="text-2xl md:text-4xl font-black uppercase tracking-tight text-white leading-tight" data-testid="blog-post-title">
            {post.title}
          </h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {post.cover_image && (
          <img
            src={`${API_URL}${post.cover_image}`}
            alt={post.title}
            className="w-full aspect-video object-cover border-2 border-black rounded-xl shadow-[4px_4px_0px_#000] mb-8"
            data-testid="blog-post-cover"
          />
        )}
        {/* Sections */}
        <article data-testid="blog-post-body">
          {post.sections.map((s, i) => (
            <section key={i} className="mb-8">
              <h2 className="text-xl md:text-2xl font-black text-neutral-900 dark:text-white mb-3">{s.heading}</h2>
              <div
                className="prose-sm text-[15px] leading-relaxed text-neutral-700 dark:text-neutral-300 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_li]:mb-1 [&_strong]:text-neutral-900 dark:[&_strong]:text-white"
                dangerouslySetInnerHTML={{ __html: s.body_html }}
              />
            </section>
          ))}
        </article>

        {/* CTA */}
        <div className="bg-[#0F172A] border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#D95800] my-8" data-testid="blog-cta">
          <p className="text-white font-black uppercase text-lg">Ready to build with confidence?</p>
          <p className="text-neutral-400 text-sm mt-1">Find vetted pros, protect payments with escrow, and shop tools & materials — all on ConstructHelp.</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Link to="/signup" className="bg-[#D95800] text-white font-black uppercase text-xs px-5 py-2.5 border-2 border-black">Get Started Free</Link>
            <Link to="/marketplace" className="bg-white text-neutral-900 font-black uppercase text-xs px-5 py-2.5 border-2 border-black">Browse Marketplace</Link>
          </div>
        </div>

        {/* FAQs */}
        {post.faqs?.length > 0 && (
          <div className="mb-10" data-testid="blog-post-faqs">
            <h2 className="text-xl font-black uppercase text-neutral-900 dark:text-white mb-4">Frequently Asked Questions</h2>
            <div className="space-y-2">
              {post.faqs.map((f, i) => (
                <div key={i} className="bg-white dark:bg-neutral-800 border-2 border-black rounded-lg">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between gap-3 text-left px-4 py-3 font-bold text-sm text-neutral-900 dark:text-white"
                    data-testid={`blog-faq-${i}`}
                  >
                    {f.q}
                    <CaretDown size={16} weight="bold" className={`shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && (
                    <p className="px-4 pb-4 text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{f.a}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Related */}
        {related.length > 0 && (
          <div data-testid="blog-related">
            <h2 className="text-lg font-black uppercase text-neutral-900 dark:text-white mb-3">Keep Reading</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="group bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4 hover:-translate-y-0.5 transition-transform">
                  {r.cover_image && (
                    <img src={`${API_URL}${r.cover_image}`} alt={r.title} loading="lazy" className="w-full aspect-video object-cover border border-black rounded-lg mb-2" />
                  )}
                  <span className={`${CATEGORY_COLORS[r.category] || 'bg-neutral-700'} text-white text-[9px] font-black uppercase px-1.5 py-0.5 border border-black rounded`}>{r.category_label}</span>
                  <p className="font-bold text-sm text-neutral-900 dark:text-white mt-2 leading-snug group-hover:text-[#D95800] transition-colors line-clamp-3">{r.title}</p>
                  <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-[#D95800] mt-2">Read <ArrowRight size={11} weight="bold" /></span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
